import { Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import * as yup from "yup";

import { useRoleListByProfileType } from "../../../api/admin";
import { useProfileListByType, useProfileTypeList, UserProfile } from "../../../api/profile";
import { Loader } from "../../../common/components/core";
import {
    FormContainer,
    FormFooter,
    InputField,
    SelectField,
} from "../../../common/components/form";
import { FormSubmissionHandler } from "../../../types/common";
import { isUserAdmin, parseRoleToText } from "../../../utils/role";
import { parseUserProfileTypeToText } from "../../../utils/userProfile";
import { ProjectUserRole } from "../../../types/project";
import { useSelector } from "../../../redux/hooks";
import { currentRolesSelector } from "../../../redux/slices/auth/selectors";
import { SelectOption } from "../../../common/components/form/Select";
import { ProfileType } from "../../EntityManagement/ProfileManageMentView";
import userCurrentApi from "../../../api/userCurrent";
import { UserCurrent } from "../../../types/userCurrent";



export type CreateNewUserFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    profileType: string;
    userProfileId: string;
    roleType: string;
};

const schema: yup.SchemaOf<CreateNewUserFormValues> = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().email("Invalid email address format.").required(),
    profileType: yup.string().required(),
    userProfileId: yup.string().required(),
    roleType: yup.string().required(),
});

type CreateNewUserFormProps = {
    onCancel: () => void;
    onSubmit: FormSubmissionHandler<CreateNewUserFormValues>;
};


const CreateNewUserForm = ({
    onCancel,
    onSubmit,
}: CreateNewUserFormProps): JSX.Element => {
    const [ profileType, setProfileType ] = useState<string>();
    const { data: profileList, isValidating: isValidatingProfileList } = useProfileTypeList();
    const { data: allProfileNameList } = useProfileListByType(profileType);
    const { data: roleList } = useRoleListByProfileType(profileType);
    const currentRoles = useSelector(currentRolesSelector);
    const isAdmin = isUserAdmin(currentRoles);       

    const [ currentProfile,setcurrentProfile ] = useState<UserProfile | null>(null);
    useEffect(() => {
        const fetch = async () => {
            const res: UserCurrent = await userCurrentApi.getCurrentUser();
            const profile: UserProfile = res.userProfile;
            setcurrentProfile(profile);
        }
        fetch();
    },[isAdmin]);

    const profileOptions = useMemo(() => {
        const profileDisabledList = [ProfileType.HSBC, ProfileType.APPROVER];

        return profileList?.reduce<SelectOption[]>((options, profile) => {
            const option = {
                value: profile,
                label: parseUserProfileTypeToText(profile),
            };

            if (isAdmin && profileDisabledList.includes(profile))
                return options;

            return [...options, option];
        }, []);
    }, [isAdmin, profileList]);

    const profileNameOptions = useMemo(() => {

        return allProfileNameList?.reduce<SelectOption[]>((options,profile) => {
            const option = {
                value: profile.id?.toString() || "",
                label: profile.name,
                isDisabled: false,
            }
            if (isAdmin) {
                if (currentProfile && currentProfile.id === profile.id) {
                    return [option];
                } else {
                    return [...options];
                }
            }
            
            return [...options,option];
        },[]);
    },[isAdmin,allProfileNameList,currentProfile]);

    const roleOptions = useMemo(() => {
        const roleDisabledList = [ProjectUserRole.ADMIN];

        return roleList?.reduce<SelectOption[]>((options, role) => {
            const option = {
                value: role,
                label: parseRoleToText(role),
            };

            if (isAdmin && roleDisabledList.includes(role)) return options;

            return [...options, option];
        }, []);
    }, [isAdmin, roleList]);

    return (
        <Loader overlay={true} isLoading={isValidatingProfileList}>
            {profileList && (
                <Formik
                    validationSchema={schema}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={onSubmit}
                    initialValues={{
                        firstName: "",
                        lastName: "",
                        email: "",
                        profileType: "",
                        userProfileId: "",
                        roleType: "",
                    }}
                >
                    {({
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                        values,
                    }) => (
                        <Loader overlay={true} isLoading={isSubmitting}>
                            <FormContainer>
                                <h1 className="mb-3 rem-1-5">
                                    Enter Details for New User
                                </h1>
                                <Form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={handleSubmit}
                                >
                                    <InputField
                                        name="firstName"
                                        label="First Name"
                                        type="text"
                                        maxLength={64}
                                        onBlur={() =>
                                            setFieldValue(
                                                "firstName",
                                                values.firstName.trim(),
                                            )
                                        }
                                        required={true}
                                    />
                                    <InputField
                                        name="lastName"
                                        label="Last Name"
                                        type="text"
                                        maxLength={64}
                                        onBlur={() =>
                                            setFieldValue(
                                                "lastName",
                                                values.lastName.trim(),
                                            )
                                        }
                                        required={true}
                                    />
                                    <InputField
                                        name="email"
                                        label="Email address"
                                        type="text"
                                        maxLength={64}
                                        onBlur={() =>
                                            setFieldValue(
                                                "email",
                                                values.email.trim(),
                                            )
                                        }
                                        required={true}
                                    />
                                    <SelectField
                                        name="profileType"
                                        label="Profile Type"
                                        options={profileOptions || []}
                                        onChange={(data: any) => {
                                            setFieldValue("userProfileId", "");
                                            setFieldValue("roleType", "");
                                            data && setProfileType(data.value);
                                        }}
                                        required={true}
                                    />
                                    <SelectField
                                        name="userProfileId"
                                        label="Profile Name"
                                        options={profileNameOptions || []}
                                        disabled={!values.profileType}
                                        required={true}
                                    />
                                    <SelectField
                                        name="roleType"
                                        label="Role Type"
                                        options={roleOptions || []}
                                        disabled={!values.profileType}
                                        required={true}
                                    />
                                    <FormFooter className="d-flex justify-content-between">
                                        <Button
                                            variant="light.tertiary"
                                            onClick={onCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button variant="light.primary" type="submit">
                                            Create
                                        </Button>
                                    </FormFooter>
                                </Form>
                            </FormContainer>
                        </Loader>
                    )}
                </Formik>
            )}
        </Loader>


    );
};

export default CreateNewUserForm;
